import Decimal from "decimal.js";
import { PlusIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  JOURNAL_ENTRY_STATUS,
  type JournalEntry,
  type JournalLine,
  type Transaction,
} from "@/apis/gql/types";
import { Button } from "@/components/ui/button";
import { ErrorMessage } from "@/components/ui/error";
import { LoadingSpinner } from "@/components/ui/spinner";
import { cn, formatAmount } from "@/lib/utils";
import { useGetAllJournalEntries } from "@/queries/ledger/get-all";

import { AccountEntityType } from "@/lib/constants/account-types";
import { BankBookingsFooter } from "./bank-bookings-footer";
import { BookingLineForm } from "./booking-line-form";
import { BookingTextForm } from "./booking-text-form";
import { DocumentNumberForm } from "./document-number-form";
import { BankBookingsNavigation } from "./navigation";
import { SuperAdminTools } from "./super-admin-tools";

type Props = {
  transactions: Transaction[];
  onConfirmBookings?: (bankTxnIds?: string[]) => Promise<void>;
};

export const BankBookings = ({ transactions, onConfirmBookings }: Props) => {
  const { data, loading, error, refetch } = useGetAllJournalEntries({
    query: {
      documentReference: {
        value: transactions.map((transaction) => transaction.bankTxnId),
        op: "in",
      },
    },
    page: 0,
    limit: 9999, // hackity hack, there is no way to get to this amounts of bookings in a transaction.
    offset: 0,
    sort: "createdAt",
    order: "desc",
    fetchPolicy: "network-only",
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-full">
        <ErrorMessage retry={refetch} />
      </div>
    );
  }

  return (
    <BankBookingsNavigation className="flex flex-col h-full min-h-0">
      {({ onKeyDown }) => (
        <div className="flex flex-col h-full min-h-0">
          <div className="flex-1 flex flex-col gap-4 min-h-0 py-2 px-4">
            <div className="flex-1 relative min-h-0">
              <div className="absolute inset-0 overflow-y-auto space-y-2">
                {[...(data ?? [])]
                  .sort((a, b) => (a.number ?? 0) - (b.number ?? 0))
                  .map((journalEntry: JournalEntry) => (
                    <BookingsTable
                      key={journalEntry.journalEntryId}
                      journalEntry={journalEntry}
                      onKeyDown={onKeyDown}
                      disabled={
                        journalEntry.status === JOURNAL_ENTRY_STATUS.POSTED
                      }
                    />
                  ))}
              </div>
            </div>
            <SuperAdminTools transactions={transactions} refetch={refetch} />
          </div>
          <BankBookingsFooter
            transactions={transactions}
            onConfirmBookings={onConfirmBookings}
            journalLines={
              data
                ?.filter(
                  (journalEntry) =>
                    journalEntry.status !== JOURNAL_ENTRY_STATUS.POSTED,
                )
                .flatMap((journalEntry) => journalEntry.journalLines) ?? []
            }
            // TODO: handle multiple currencies
            currency="EUR"
          />
        </div>
      )}
    </BankBookingsNavigation>
  );
};

type BookingsTableProps = {
  journalEntry: JournalEntry;
  disabled: boolean;
  onKeyDown: (
    e: React.KeyboardEvent<HTMLInputElement | HTMLButtonElement>,
  ) => void;
};

const BookingsTable = ({
  journalEntry,
  onKeyDown,
  disabled,
}: BookingsTableProps) => {
  const { t } = useTranslation();
  const [newLines, setNewLines] = useState<JournalLine[]>([]);

  const lines = useMemo(() => {
    return journalEntry.journalLines;
  }, [journalEntry]);

  const allLines = useMemo(() => {
    return [...lines, ...newLines].sort((a, b) => {
      const aIsBankAccount = a.journalAccount?.entityType === "BANK_ACCOUNT";
      const bIsBankAccount = b.journalAccount?.entityType === "BANK_ACCOUNT";

      if (aIsBankAccount && !bIsBankAccount) return -1;
      if (!aIsBankAccount && bIsBankAccount) return 1;

      return (a.number ?? 0) - (b.number ?? 0);
    });
  }, [lines, newLines]);

  const totalDebit = useMemo(() => {
    return lines.reduce(
      (acc, line) => new Decimal(acc).plus(line.debit ?? 0),
      new Decimal(0),
    );
  }, [lines]);

  const totalCredit = useMemo(() => {
    return lines.reduce(
      (acc, line) => new Decimal(acc).plus(line.credit ?? 0),
      new Decimal(0),
    );
  }, [lines]);

  const handleAddLine = () => {
    const amount = new Decimal(totalDebit).minus(totalCredit).toNumber();

    const newLine: JournalLine = {
      journalLineId: `new-${Date.now()}`,
      journalEntryId: journalEntry.journalEntryId,
      journalAccount: {
        journalAccountId: "",
        tenantId: journalEntry.tenantId,
        number: null,
        accountType: null,
        name: null,
        entity: null,
        accountEntity: null,
        entityType: null,
        entityId: null,
        supplier: null,
        currency: "EUR",
        account: null,
        bankAccount: null,
      },
      account: null,
      taxCodeId: null,
      costCenterId: null,
      debit: amount < 0 ? Math.abs(amount) : null,
      credit: amount > 0 ? Math.abs(amount) : null,
      originalAmount: amount,
      originalCurrency: "EUR",
      currency: "EUR",
      number: 999999,
      tenantId: journalEntry.tenantId,
      createdAt: new Date(),
      updatedAt: new Date(),
      deletedAt: null,
      taxCode: null,
      costCenter: null,
    };
    setNewLines((prev) => [...prev, newLine]);
  };

  const handleRemoveLine = (lineId: string) => {
    setNewLines((prev) => prev.filter((line) => line.journalLineId !== lineId));
  };

  const difference = useMemo(() => {
    return totalDebit.minus(totalCredit);
  }, [totalDebit, totalCredit]);

  const isBalanced = difference.equals(0);

  return (
    <div className="flex flex-col gap-2">
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="w-full">
          <div className="overflow-x-auto">
            <div className="min-w-[700px]">
              <div className="bg-gray-50 border-b border-gray-200">
                <div className="grid grid-cols-[minmax(200px,1fr)_140px_140px_140px_auto] gap-2 items-center px-2.5">
                  <div className="py-1.5 text-left text-xs font-medium text-gray-500">
                    {t("transactions.bank_bookings.table.account")}
                  </div>
                  <div className="py-1.5 text-left text-xs font-medium text-gray-500">
                    {t("transactions.bank_bookings.table.debit")}
                  </div>
                  <div className="py-1.5 text-left text-xs font-medium text-gray-500">
                    {t("transactions.bank_bookings.table.credit")}
                  </div>
                  <div className="py-1.5 text-left text-xs font-medium text-gray-500">
                    {t("transactions.bank_bookings.table.tax_code")}
                  </div>
                  <div className="py-1.5 flex items-center justify-end">
                    <div className="w-8" />
                  </div>
                </div>
              </div>

              <div>
                <div className="py-1">
                  {allLines.map((line) => (
                    <BookingLineForm
                      journalEntryId={journalEntry.journalEntryId}
                      key={line.journalLineId}
                      line={line}
                      onRemove={() => handleRemoveLine(line.journalLineId)}
                      onKeyDown={onKeyDown}
                      disabled={
                        disabled ||
                        line.journalAccount.entityType ===
                          AccountEntityType.BANK_ACCOUNT
                      }
                    />
                  ))}
                </div>

                <div
                  className={cn(
                    "grid grid-cols-[minmax(200px,1fr)_140px_140px_140px_auto] gap-2 items-center bg-gray-50 border-t border-b border-gray-200 px-2.5",
                    !isBalanced && "bg-amber-50 border-amber-200",
                  )}
                >
                  <div className="py-1.5 text-xs font-medium text-gray-600" />
                  <div
                    className={cn(
                      "py-1.5 text-right text-xs font-medium text-gray-600",
                      !isBalanced && "text-amber-700",
                    )}
                  >
                    {formatAmount(totalDebit.toNumber(), "EUR")}
                  </div>
                  <div
                    className={cn(
                      "py-1.5 text-right text-xs font-medium text-gray-600",
                      !isBalanced && "text-amber-700",
                    )}
                  >
                    {formatAmount(totalCredit.toNumber(), "EUR")}
                  </div>
                  <div className="py-1.5" />
                  <div className="py-1.5 flex items-center justify-end">
                    <div className="w-8" />
                  </div>
                </div>

                <div className="flex flex-row justify-between">
                  <div className="bg-white">
                    <div className="px-4 py-1.5">
                      <DocumentNumberForm
                        journalEntryId={journalEntry.journalEntryId}
                        documentNumber={journalEntry.documentNumber}
                        onKeyDown={onKeyDown}
                        disabled={disabled}
                      />
                    </div>
                  </div>

                  <div className="bg-white">
                    <div className="px-4 py-1.5">
                      <BookingTextForm
                        journalEntryId={journalEntry.journalEntryId}
                        bookingText={journalEntry.bookingText}
                        onKeyDown={onKeyDown}
                        disabled={disabled}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {!disabled && (
        <Button
          onClick={handleAddLine}
          variant="outline"
          size="sm"
          className="self-end px-1.5 py-1 rounded-lg bg-white hover:bg-gray-50 transition-colors border border-gray-300 hover:border-gray-400 text-xs text-gray-700 flex items-center gap-1 h-7"
        >
          <PlusIcon className="w-3.5 h-3.5" />
          {t("transactions.bank_bookings.table.add_line")}
        </Button>
      )}
    </div>
  );
};
